import type { TFunction } from "react-i18next";
import type { JournalFieldUi } from "./journalRank";

/**
 * The reader's name of each of the 101 research fields a journal is ranked in
 * (`apps/api/src/domain/journal/fields.ts`, docs/journal-fields.md).
 *
 * The api sends the field as its MeSH descriptor UI. The English names below
 * are the fields' `display`, the name the placement model and the admin read;
 * the reader needs it in the app's language, which is a job for i18next.
 *
 * Every value calls the translator on a bare string literal, and the parameter
 * is named `t`, because i18next-scanner extracts keys only from call sites
 * spelled exactly that way and prunes every key it cannot see
 * (`removeUnusedKeys`).
 *
 * Keyed by the descriptor UI union the api infers, so a field added to the
 * list without a name here fails the type check. `fields.test.ts` in the api
 * checks that every display is a key of the English locale file and is
 * translated in all eight languages.
 */
export const JOURNAL_FIELD_NAMES = {
  D007388: (t) => t("General Internal Medicine"),
  D011320: (t) => t("Primary Health Care"),
  D002309: (t) => t("Cardiology"),
  D014652: (t) => t("Vascular Diseases"),
  D015272: (t) => t("Pulmonary Medicine"),
  D005762: (t) => t("Gastroenterology"),
  D004704: (t) => t("Endocrinology"),
  D009398: (t) => t("Nephrology"),
  D014572: (t) => t("Urology"),
  D012219: (t) => t("Rheumatology"),
  D000486: (t) => t("Allergy and Immunology"),
  D055552: (t) => t("Infectious Disease Medicine"),
  D014330: (t) => t("Tropical Medicine"),
  D006405: (t) => t("Hematology"),
  D008495: (t) => t("Medical Oncology"),
  D003880: (t) => t("Dermatology"),
  D009462: (t) => t("Neurology"),
  D011570: (t) => t("Psychiatry"),
  D000073316: (t) => t("Addiction Medicine"),
  D011586: (t) => t("Clinical Psychology"),
  D010372: (t) => t("Pediatrics"),
  D009774: (t) => t("Obstetrics"),
  D006176: (t) => t("Gynecology"),
  D018456: (t) => t("Reproductive Medicine"),
  D005853: (t) => t("Geriatrics"),
  D009885: (t) => t("Ophthalmology"),
  D010036: (t) => t("Otolaryngology"),
  D001298: (t) => t("Audiology"),
  D013066: (t) => t("Speech-Language Pathology"),
  D003813: (t) => t("Dentistry"),
  D055048: (t) => t("Integrative Medicine"),
  D014730: (t) => t("Veterinary Medicine"),
  D013502: (t) => t("General Surgery"),
  D009985: (t) => t("Orthopedics"),
  D014180: (t) => t("Transplantation"),
  D000776: (t) => t("Anesthesiology"),
  D003422: (t) => t("Critical Care"),
  D004635: (t) => t("Emergency Medicine"),
  D010810: (t) => t("Physical and Rehabilitation Medicine"),
  D013178: (t) => t("Sports Medicine"),
  D011871: (t) => t("Radiology"),
  D059906: (t) => t("Neuroimaging"),
  D010336: (t) => t("Pathology"),
  D013677: (t) => t("Medical Laboratory Science"),
  D011634: (t) => t("Public Health"),
  D006302: (t) => t("Health Services Research"),
  D006291: (t) => t("Health Policy"),
  D009729: (t) => t("Nursing"),
  D052756: (t) => t("Nutritional Sciences"),
  D004501: (t) => t("Medical Education"),
  D001675: (t) => t("Bioethics"),
  D005554: (t) => t("Forensic Medicine"),
  D008490: (t) => t("Medical Informatics"),
  D000090963: (t) => t("Translational Science"),
  D010600: (t) => t("Pharmacology"),
  D010604: (t) => t("Pharmacy"),
  D002626: (t) => t("Medicinal Chemistry"),
  D014116: (t) => t("Toxicology"),
  D009488: (t) => t("Neurosciences"),
  D001525: (t) => t("Behavioral Sciences"),
  D010827: (t) => t("Physiology"),
  D000715: (t) => t("Anatomy"),
  D005823: (t) => t("Genetics"),
  D003585: (t) => t("Cell Biology"),
  D015509: (t) => t("Developmental Biology"),
  D001671: (t) => t("Biochemistry"),
  D001703: (t) => t("Biophysics"),
  D019295: (t) => t("Computational Biology"),
  D008829: (t) => t("Microbiology"),
  D014773: (t) => t("Virology"),
  D010274: (t) => t("Parasitology"),
  D009172: (t) => t("Mycology"),
  D001709: (t) => t("Biotechnology"),
  D001698: (t) => t("Biomedical Engineering"),
  D044968: (t) => t("Regenerative Medicine"),
  D001672: (t) => t("Biomaterials"),
  D001695: (t) => t("Biology"),
  D004463: (t) => t("Ecology"),
  D008386: (t) => t("Marine Biology"),
  D000383: (t) => t("Agriculture"),
  D001901: (t) => t("Botany"),
  D005524: (t) => t("Food Technology"),
  D000079265: (t) => t("Environmental Science"),
  D002621: (t) => t("Chemistry"),
  D010825: (t) => t("Physics"),
  D001256: (t) => t("Astronomy"),
  D000074266: (t) => t("Materials Science"),
  D056448: (t) => t("Earth Sciences"),
  D008433: (t) => t("Mathematics"),
  D003205: (t) => t("Computing Methodologies"),
  D004738: (t) => t("Engineering"),
  D011584: (t) => t("Psychology"),
  D004493: (t) => t("Education"),
  D004467: (t) => t("Economics"),
  D003132: (t) => t("Business"),
  D007603: (t) => t("Jurisprudence"),
  D011057: (t) => t("Politics"),
  D012942: (t) => t("Social Sciences"),
  D006809: (t) => t("Humanities"),
  D000075762: (t) => t("Multidisciplinary Research"),
  D010811: (t) => t("Natural Sciences"),
} as const satisfies Record<JournalFieldUi, (t: TFunction) => string>;
